import React, { useEffect, useState } from "react";
import {
  EditOutlined,
  NumberOutlined,
  PlusOutlined,
  SaveOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Button,
  Empty,
  Input,
  Modal,
  Popover,
  Select,
  Spin,
  Tooltip,
} from "antd";
import { useSelector } from "react-redux";

// import utils
import openNotification from "../../components/Notifications/openNotification";
import isEmpty from "../../validations/is-empty";
import apiClient from "../../actions/api";
import { socket } from "../../untils/socket";

const MemberList = ({
  setSelectedChat,
  selectedChat,
  onlineUsers,
  setJoinedUsers,
  setGroupList,
  unreadCounts,
  markConversationRead,
}) => {
  const authUser = useSelector((state) => state.auth.user);
  const themeMode = useSelector((state) => state.themeMode);

  const [channelLoading, setChannelLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [userList, setUserList] = useState([]);
  const [channelList, setChannelList] = useState([]);
  const [channelModelOpen, setChannelModelOpen] = useState(false);
  const [channelName, setChannelName] = useState("");
  const [userChannelList, setUserChannelList] = useState([]);
  const [selectUserList, setSelectUserList] = useState([]);
  const [editingChannel, setEditingChannel] = useState(null);
  const [editChannelName, setEditChannelName] = useState("");
  const [editMemberList, setEditMemberList] = useState([]);

  useEffect(() => {
    if (!isEmpty(authUser)) {
      getInit();

      socket.on("new_channel_notification", () => getChannelList(authUser._id));

      return () => {
        socket.off("new_channel_notification");
      };
    }
  }, [authUser]);

  const getChannelList = async (userId) => {
    setChannelLoading(true);

    try {
      const res = await apiClient.post("/chats/get-groups", { userId });
      setChannelList(res.data);
      setGroupList(res.data);
      socket.emit("join_rooms");
    } catch (err) {
      if (!isEmpty(err?.response?.data?.errors)) {
        err.response.data.errors.forEach((item) => {
          openNotification("error", "Validation Error!", item.msg);
        });
      }
    } finally {
      setChannelLoading(false);
    }
  };

  const getUserList = async () => {
    setUserLoading(true);

    try {
      const res = await apiClient.get("/users/get-users");
      const otherUsers = res.data.filter((item) => item._id !== authUser._id);
      setUserList(otherUsers);
      setJoinedUsers(res.data);
      const tempUserChannelList = res.data.map((item) => {
        return {
          value: item._id,
          label: item.name,
        };
      });
      setUserChannelList(tempUserChannelList);
      setSelectUserList(tempUserChannelList);
    } catch (err) {
      if (!isEmpty(err?.response?.data?.errors)) {
        err.response.data.errors.forEach((item) => {
          openNotification("error", "Validation Error!", item.msg);
        });
      }
    } finally {
      setUserLoading(false);
    }
  };

  const getInit = async () => {
    setInitialLoading(true);
    try {
      await Promise.all([getUserList(), getChannelList(authUser._id)]);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChannelClick = (item) => {
    socket.emit("join_room", item._id);
    setSelectedChat({ type: "group", id: item._id, name: item.name });
    markConversationRead("group", item._id);
  };

  const handleUserClick = (item) => {
    setSelectedChat({ type: "dm", id: item._id, name: item.name });
    markConversationRead("dm", item._id);
  };

  const handleChannelModelOpenChange = (newOpen) => {
    setChannelModelOpen(newOpen);
  };

  const handleCreateChannel = () => {
    if (isEmpty(channelName)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please type Channel Name.",
      );
      setChannelName("");
    } else if (selectUserList.length === 0) {
      openNotification(
        "error",
        "Validation Error!",
        "Please select at least one user.",
      );
    } else {
      const params = {
        name: channelName,
        selectUserList: selectUserList,
      };

      setChannelLoading(true);

      apiClient
        .post("/chats/add-group", params)
        .then((res) => {
          if (res.data.status === "success") {
            getChannelList(authUser._id);
            setChannelName("");
            setSelectUserList(userChannelList);
            setChannelModelOpen(false);
            socket.emit("created_new_channel", res.data.group._id);
          } else {
            setChannelLoading(false);
          }
        })
        .catch((err) => {
          if (!isEmpty(err?.response?.data?.errors)) {
            err.response.data.errors.forEach((item) => {
              openNotification("error", "Validation Error!", item.msg);
            });
          }
          setChannelLoading(false);
        });
    }
  };

  const openChannelEditor = (item) => {
    const memberIds = (item.enabled_users || []).map(String);
    setEditingChannel(item);
    setEditChannelName(item.name || "");
    setEditMemberList(
      userChannelList.filter((user) => memberIds.includes(String(user.value))),
    );
  };

  const handleUpdateChannel = async () => {
    const name = editChannelName.trim();
    if (!name || editMemberList.length === 0) {
      openNotification(
        "error",
        "Validation Error!",
        name
          ? "Please select at least one teammate."
          : "Please type a channel name.",
      );
      return;
    }

    setChannelLoading(true);
    try {
      const { data } = await apiClient.put(
        `/chats/groups/${editingChannel._id}`,
        {
          name,
          memberIds: editMemberList.map((item) => item.value),
        },
      );
      await getChannelList(authUser._id);
      if (
        selectedChat?.type === "group" &&
        selectedChat.id === editingChannel._id
      ) {
        setSelectedChat({ ...selectedChat, name: data.group.name });
      }
      setEditingChannel(null);
      socket.emit("created_new_channel", data.group._id);
      openNotification(
        "success",
        "Channel updated",
        "Channel name and teammates were saved.",
      );
    } catch (err) {
      (
        err?.response?.data?.errors || [{ msg: "Unable to update channel." }]
      ).forEach((item) => openNotification("error", "Update failed", item.msg));
    } finally {
      setChannelLoading(false);
    }
  };

  return (
    <div className="member-list-section">
      {(initialLoading || userLoading || channelLoading) && (
        <div className="member-list-loading">
          <div className="member-list-loading-indicator">
            <Spin size="large" />
            <span>Loading conversations...</span>
          </div>
        </div>
      )}
      <div className="chat-sidebar-brand">
        <div className="chat-sidebar-brand-icon">
          <TeamOutlined />
        </div>
        <div>
          <div className="chat-sidebar-brand-title">Team chat</div>
          <div className="chat-sidebar-brand-subtitle">
            {onlineUsers.length} online
          </div>
        </div>
      </div>
      <div className="member-list-scroll">
        <div
          className={`member-header ${
            themeMode.themeMode ? "member-dark-header" : "member-light-header"
          }`}
        >
          <span>Channels</span>
          {authUser.role === 1 && (
            <Popover
              trigger="click"
              open={channelModelOpen}
              onOpenChange={handleChannelModelOpenChange}
              placement="rightTop"
              overlayClassName="channel-create-popover"
              content={
                <div className="channel-add-modal-section">
                  <div className="channel-add-modal-heading">
                    <div className="channel-add-modal-icon">
                      <NumberOutlined />
                    </div>
                    <div>
                      <strong>Create a channel</strong>
                      <span>Organize a focused space for your team.</span>
                    </div>
                  </div>
                  <div className="channel-add-modal-section-field">
                    <div className="channel-add-modal-field-title">
                      Channel name
                    </div>
                    <Input
                      placeholder="e.g. daily-updates"
                      size="large"
                      style={{ width: "100%" }}
                      value={channelName}
                      onChange={(e) => setChannelName(e.target.value)}
                    />
                  </div>
                  <div className="channel-add-modal-section-field">
                    <div className="channel-add-modal-field-title">Members</div>
                    <Select
                      mode="multiple"
                      labelInValue
                      size="large"
                      placeholder="Choose channel members"
                      maxTagCount="responsive"
                      value={selectUserList}
                      onChange={(value) => setSelectUserList(value)}
                      style={{ width: "100%" }}
                      options={userChannelList}
                    />
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    icon={<SaveOutlined />}
                    className="channel-create-submit"
                    onClick={() => handleCreateChannel()}
                    loading={channelLoading}
                    disabled={channelLoading}
                  >
                    Create
                  </Button>
                </div>
              }
            >
              <Tooltip title="Add Channel">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  className="add-channel-button"
                >
                  <span>New</span>
                </Button>
              </Tooltip>
            </Popover>
          )}
        </div>
        <div className="channel-box">
          {isEmpty(channelList) ? (
            <div className="channel-box-empty">
              <Empty description="No Channel" />
            </div>
          ) : (
            channelList.map((item) => {
              return (
                <div className="channel-row" key={item._id}>
                  <button
                    className={`channel-item ${
                      themeMode.themeMode
                        ? "channel-item-dark"
                        : "channel-item-light"
                    } ${
                      !isEmpty(selectedChat)
                        ? selectedChat.type === "group"
                          ? selectedChat.id === item._id
                            ? "channel-item-active"
                            : ""
                          : ""
                        : ""
                    }`}
                    onClick={() => handleChannelClick(item)}
                  >
                    <span className="channel-icon">
                      <NumberOutlined />
                    </span>
                    <span className="channel-name">{item.name}</span>
                    {!!unreadCounts?.channels?.[item._id] && (
                      <span className="chat-unread-badge">
                        {unreadCounts.channels[item._id]}
                      </span>
                    )}
                  </button>
                  {Number(authUser.role) === 1 && (
                    <Tooltip title="Edit channel">
                      <button
                        type="button"
                        className="channel-edit-button"
                        aria-label={`Edit ${item.name}`}
                        onClick={() => openChannelEditor(item)}
                      >
                        <EditOutlined />
                      </button>
                    </Tooltip>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div
          className={`member-header ${
            themeMode.themeMode ? "member-dark-header" : "member-light-header"
          }`}
        >
          <span>Direct messages</span>
        </div>
        <div className="dm-box">
          {isEmpty(userList) ? (
            <div className="dm-box-empty">
              <Empty description="No User" />
            </div>
          ) : (
            userList.map((item) => {
              const isOnline = onlineUsers.includes(item._id);

              return (
                <button
                  className={`dm-item ${
                    themeMode.themeMode ? "dm-item-dark" : "dm-item-light"
                  } ${
                    !isEmpty(selectedChat)
                      ? selectedChat.type === "dm"
                        ? selectedChat.id === item._id
                          ? "dm-item-active"
                          : ""
                        : ""
                      : ""
                  }`}
                  key={item._id}
                  onClick={() => handleUserClick(item)}
                >
                  <div className="person-avatar">
                    <img
                      src={item.avatar}
                      alt="avatar"
                      className="person-img"
                    />
                    <div
                      className={`online-status ${
                        isOnline
                          ? "online-status-active"
                          : "online-status-inactive"
                      }`}
                    ></div>
                  </div>
                  <div className="person-name">{item.name}</div>
                  <span className="person-presence">
                    {isOnline ? "Online" : "Offline"}
                  </span>
                  {!!unreadCounts?.directMessages?.[item._id] && (
                    <span className="chat-unread-badge">
                      {unreadCounts.directMessages[item._id]}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
      <Modal
        title="Edit channel"
        open={Boolean(editingChannel)}
        onCancel={() => setEditingChannel(null)}
        onOk={handleUpdateChannel}
        okText="Save changes"
        confirmLoading={channelLoading}
        destroyOnHidden
        className="channel-edit-modal"
      >
        <div className="channel-edit-fields">
          <label>
            <span>Channel name</span>
            <Input
              value={editChannelName}
              onChange={(event) => setEditChannelName(event.target.value)}
              maxLength={80}
              placeholder="Channel name"
            />
          </label>
          <label>
            <span>Teammates</span>
            <Select
              mode="multiple"
              labelInValue
              value={editMemberList}
              onChange={setEditMemberList}
              options={userChannelList}
              maxTagCount="responsive"
              placeholder="Choose channel teammates"
              style={{ width: "100%" }}
            />
          </label>
        </div>
      </Modal>
    </div>
  );
};

export default MemberList;
